import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import seoAiService, { TitleMetaSuggestion } from '@/services/seoAi';

type Props = {
  keyword: string;
  pageUrl?: string;
  position?: number;
  ctr?: number;
  impressions?: number;
};

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5 }}>
        {copied ? <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
      </IconButton>
    </Tooltip>
  );
};

const CharCount = ({ length, min, max }: { length: number; min: number; max: number }) => {
  const isGood = length >= min && length <= max;
  return (
    <Typography variant="caption" color={isGood ? 'success.main' : 'warning.main'}>
      {length} chars {isGood ? '' : length < min ? '(too short)' : '(too long)'}
    </Typography>
  );
};

const AiTitleMetaRewriter = function ({ keyword, pageUrl, position, ctr, impressions }: Props): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<TitleMetaSuggestion[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await seoAiService.rewriteTitleMeta({
        keyword,
        currentTitle: '',
        currentMeta: '',
        pageUrl,
        position,
        ctr,
        impressions,
      });
      setSuggestions(result.suggestions ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate suggestions');
    } finally {
      setIsLoading(false);
    }
  };

  if (!suggestions && !isLoading) {
    return (
      <Button
        size="small"
        startIcon={<AutoFixHighIcon />}
        onClick={handleGenerate}
        sx={{ fontSize: 12, textTransform: 'none', color: '#054AD9', mt: 0.5 }}
      >
        AI: Suggest better title & meta
      </Button>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, py: 1 }}>
        <CircularProgress size={14} sx={{ color: '#054AD9' }} />
        <Typography variant="caption" color="text.secondary">Generating optimized titles & metas...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="error">{error}</Typography>
        <Button size="small" onClick={handleGenerate} sx={{ fontSize: 11, ml: 1 }}>Retry</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1.5, borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
        <AutoFixHighIcon sx={{ fontSize: 16, color: '#054AD9' }} />
        <Typography variant="caption" fontWeight={700} color="#054AD9">AI Suggestions</Typography>
        <Button size="small" onClick={handleGenerate} sx={{ fontSize: 11, ml: 'auto', textTransform: 'none' }}>
          Regenerate
        </Button>
      </Box>

      {suggestions?.map((s, i) => (
        <Box
          key={i}
          sx={{
            mb: 1.5,
            p: 1.5,
            backgroundColor: '#fafafa',
            borderRadius: 2,
            border: '1px solid',
            borderColor: i === 0 ? '#054AD930' : 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <Chip label={`Option ${i + 1}`} size="small" sx={{ fontSize: 10, height: 18 }} color={i === 0 ? 'secondary' : 'default'} />
            {i === 0 && <Chip label="Recommended" size="small" sx={{ fontSize: 10, height: 18 }} color="success" />}
          </Box>

          {/* Title */}
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary">Title</Typography>
              <CharCount length={s.title.length} min={50} max={60} />
              <CopyButton text={s.title} />
            </Box>
            <Typography variant="body2" sx={{ color: '#1a0dab', fontWeight: 500, fontSize: 13 }}>
              {s.title}
            </Typography>
          </Box>

          {/* Meta */}
          <Box sx={{ mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary">Meta Description</Typography>
              <CharCount length={s.meta.length} min={150} max={160} />
              <CopyButton text={s.meta} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
              {s.meta}
            </Typography>
          </Box>

          {/* Reasoning */}
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
            {s.reasoning}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default AiTitleMetaRewriter;
